import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { statsApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import PageHeader from '../../components/PageHeader.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import Card from '../../components/Card.jsx';
import StatCard from '../../components/StatCard.jsx';
import DataTable from '../../components/DataTable.jsx';
import Badge from '../../components/Badge.jsx';
import Spinner from '../../components/Spinner.jsx';
import EmptyState from '../../components/EmptyState.jsx';
import BarChart from '../../components/charts/BarChart.jsx';
import HBarChart from '../../components/charts/HBarChart.jsx';
import { CHART_SERIES, OWNER_COLORS } from '../../components/charts/palette.js';
import { formatCurrency, formatNumber } from '../../utils/format.js';

const GRANULARITIES = ['day', 'month', 'year'];

/** Compact money for axis ticks: 12 500 -> 12.5k */
const shortMoney = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `${Math.round(n / 1000)}k`;
  return String(Math.round(n));
};

/**
 * Company-wide statistics.
 *
 * Amounts and counts are deliberately split into two charts: putting money and
 * volumes on one pair of axes is the single most misleading thing a dashboard
 * can do.
 */
const StatsPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const [granularity, setGranularity] = useState('month');

  const { data, loading, error } = useApi(() => statsApi.get({ granularity }), [granularity]);

  if (loading) return <Spinner full />;
  if (error) return <EmptyState title={t('common.error')} description={error.message} />;

  /** "2026-09" -> "Sept." ; long form for the tooltip. */
  const formatPeriod = (period, long = false) => {
    if (granularity === 'year') return period;
    if (granularity === 'month') {
      const date = new Date(`${period}-01T00:00:00.000Z`);
      return new Intl.DateTimeFormat(locale, {
        month: long ? 'long' : 'short',
        year: long ? 'numeric' : undefined,
        timeZone: 'UTC',
      }).format(date);
    }
    const date = new Date(`${period}T00:00:00.000Z`);
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: long ? 'long' : 'short',
      timeZone: 'UTC',
    }).format(date);
  };

  const money = (value, axis = false) =>
    axis ? shortMoney(value) : formatCurrency(value, locale);

  const stageRows = data.funnel
    .filter((row) => row.count > 0 || !['ON_HOLD'].includes(row.stage))
    .map((row) => ({
      key: row.stage,
      label: t(`customers.status.${row.stage}`),
      value: row.count,
      color: OWNER_COLORS[row.owner] ?? OWNER_COLORS.null,
      note: formatCurrency(row.amount, locale),
    }));

  const breakdownRows = (record, prefix) =>
    Object.entries(record ?? {})
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([key, count], index) => ({
        key,
        label: prefix ? t(`${prefix}.${key}`, key) : key,
        value: count,
        color: CHART_SERIES[index % CHART_SERIES.length],
      }));

  const userColumns = [
    {
      key: 'name',
      header: t('users.title'),
      render: (row) => (
        <div className="cell-stack">
          <span className="cell-strong">{row.name}</span>
          <Badge value={row.role} label={t(`roles.${row.role}`)} />
        </div>
      ),
    },
    { key: 'customers', header: t('dashboard.customers'), className: 'cell-right' },
    { key: 'leads', header: t('dashboard.leads'), className: 'cell-right' },
    { key: 'meetings', header: t('dashboard.appointmentsUpcoming'), className: 'cell-right' },
    { key: 'won', header: t('dashboard.won'), className: 'cell-right' },
    {
      key: 'wonAmount',
      header: t('dashboard.wonValue'),
      className: 'cell-right',
      render: (row) => formatCurrency(row.wonAmount, locale),
    },
    {
      key: 'pipelineValue',
      header: t('dashboard.pipelineValue'),
      className: 'cell-right',
      render: (row) => formatCurrency(row.pipelineValue, locale),
    },
  ];

  return (
    <>
      <PageHeader title={t('stats.title')} subtitle={`${data.from} - ${data.to}`} />

      <Toolbar>
        <div className="segmented" role="group" aria-label={t('stats.granularity')}>
          {GRANULARITIES.map((item) => (
            <button
              key={item}
              type="button"
              className={granularity === item ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
              onClick={() => setGranularity(item)}
            >
              {t(`stats.granularities.${item}`)}
            </button>
          ))}
        </div>
      </Toolbar>

      <div className="stat-grid">
        <StatCard label={t('stats.wonAmount')} value={formatCurrency(data.totals.wonAmount, locale)} tone="success" />
        <StatCard label={t('dashboard.pipelineValue')} value={formatCurrency(data.totals.openPipeline, locale)} />
        <StatCard label={t('stats.averageDeal')} value={formatCurrency(data.totals.averageDeal, locale)} />
        <StatCard label={t('dashboard.winRate')} value={`${data.totals.winRate}%`} />
        <StatCard label={t('dashboard.customers')} value={formatNumber(data.totals.customers, locale)} />
        <StatCard label={t('dashboard.leads')} value={formatNumber(data.totals.leads, locale)} />
        <StatCard label={t('stats.meetings')} value={formatNumber(data.totals.meetings, locale)} />
        <StatCard
          label={t('stats.lostAmount')}
          value={formatCurrency(data.totals.lostAmount, locale)}
          tone="danger"
        />
      </div>

      {/* Money. One series, one axis. */}
      <Card title={t('stats.revenueOverTime')}>
        <BarChart
          data={data.series}
          series={[{ key: 'wonAmount', label: t('stats.wonAmount'), color: CHART_SERIES[0] }]}
          formatValue={money}
          formatPeriod={formatPeriod}
          emptyLabel={t('common.noData')}
        />
      </Card>

      {/* Volumes. Counts only - never mixed with the money chart above. */}
      <Card title={t('stats.activityOverTime')}>
        <BarChart
          data={data.series}
          series={[
            { key: 'leads', label: t('dashboard.leads'), color: CHART_SERIES[0] },
            { key: 'meetings', label: t('stats.meetings'), color: CHART_SERIES[1] },
            { key: 'won', label: t('dashboard.won'), color: CHART_SERIES[2] },
          ]}
          formatValue={(v) => formatNumber(Math.round(v), locale)}
          formatPeriod={formatPeriod}
          emptyLabel={t('common.noData')}
        />
      </Card>

      <div className="grid grid--2">
        <Card title={t('stats.funnel')}>
          {/* Colour = which role owns the stage, so the hand-off is visible. */}
          <div className="chart__legend">
            {['TELEPRO', 'COMMERCIAL', 'ADMIN'].map((role) => (
              <span key={role} className="chart__legend-item">
                <span className="chart__legend-dot" style={{ background: OWNER_COLORS[role] }} aria-hidden="true" />
                {t(`roles.${role}`)}
              </span>
            ))}
          </div>
          <HBarChart rows={stageRows} formatValue={(v) => formatNumber(v, locale)} emptyLabel={t('common.noData')} />
        </Card>

        <Card title={t('stats.bySource')}>
          <HBarChart
            rows={breakdownRows(data.breakdown.bySource, 'customers.sources')}
            formatValue={(v) => formatNumber(v, locale)}
            emptyLabel={t('common.noData')}
          />
        </Card>

        <Card title={t('stats.byTemperature')}>
          <HBarChart
            rows={breakdownRows(data.breakdown.byTemperature, 'leads.temperatures')}
            formatValue={(v) => formatNumber(v, locale)}
            emptyLabel={t('common.noData')}
          />
        </Card>

        <Card title={t('stats.byComplexity')}>
          <HBarChart
            rows={breakdownRows(data.breakdown.byComplexity, 'leads.complexities')}
            formatValue={(v) => formatNumber(v, locale)}
            emptyLabel={t('common.noData')}
          />
        </Card>
      </div>

      <Card title={t('stats.byUser')}>
        <DataTable columns={userColumns} rows={data.byUser} emptyTitle={t('common.noData')} />
      </Card>
    </>
  );
};

export default StatsPage;
